import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./authContext.tsx";
import { groupEndpoints } from "./endpointUtils.ts";
import { getRecentEndpointIds } from "./recentEndpoints.ts";
import { ThemeSwitcher } from "../components/ui/themeSwitcher.tsx";
import { Logo } from "../components/ui/logo.tsx";
import { StatusIcon } from "../components/ui/statusIcon.tsx";
import { getBrandTitle } from "../api/client.ts";
import type { EndPoint } from "../types/session.ts";
import styles from "./endpointSelectionPage.module.scss";

export function EndpointSelectionPage() {
    const { session, endpoints, fetchEndpoints, setActiveEndpoint, logout } = useAuth();
    const [filter, setFilter] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    const groups = groupEndpoints(endpoints, filter);

    // Recent picks, filtered down to still-available endpoints and the same
    // search text the groups are filtered by.
    const recentEndpoints = useMemo<EndPoint[]>(() => {
        if (!session?.UID) return [];
        const ids = getRecentEndpointIds(session.UID);
        if (ids.length === 0) return [];
        const byId = new Map(endpoints.map((ep) => [ep.ID, ep]));
        const q = filter.trim().toLowerCase();
        const out: EndPoint[] = [];
        for (const id of ids) {
            const ep = byId.get(id);
            if (!ep) continue;
            if (q) {
                const hit = ep.Title?.toLowerCase().includes(q) || ep.ShortName?.toLowerCase().includes(q);
                if (!hit) continue;
            }
            out.push(ep);
        }
        return out;
    }, [session?.UID, endpoints, filter]);

    // Enter in the filter opens the first row shown: a recent match first, else the
    // first endpoint of the first group. Only while filtering, so an empty box never
    // opens an endpoint by accident.
    const enterTarget = filter.trim()
        ? (recentEndpoints[0] ?? groups[0]?.endpoints[0] ?? null)
        : null;
    const enterTargetIsRecent = enterTarget != null && enterTarget === recentEndpoints[0];
    const itemClass = (ep: EndPoint, isRecent: boolean) =>
        ep === enterTarget && isRecent === enterTargetIsRecent
            ? `${styles.endpointItem} ${styles.endpointItemEnterTarget}`
            : styles.endpointItem;

    useEffect(() => {
        inputRef.current?.focus();
        // Always fetch fresh: list reflects current IP-restriction state, which may have changed
        // (e.g. DEBUG IP override applied) since the list was last loaded.
        fetchEndpoints().catch(console.error);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className={styles.endpointSelectionPanel}>
            <div className={styles.themeSwitcherCorner}>
                <ThemeSwitcher />
            </div>
            <div className={styles.brandBlock}>
                <Logo className={styles.brandLogo} />
                <span className={styles.brandText}>{getBrandTitle()}</span>
            </div>
            <div className={styles.endpointCard}>
                <div className={styles.endpointCardHeader}>
                    <h2>Select endpoint</h2>
                    <button
                        onClick={logout}
                        className={styles.endpointCardLogoutBtn}
                    >
                        Logout
                    </button>
                </div>

                <div className={styles.endpointFilterWrap}>
                    <input
                        ref={inputRef}
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key !== "Enter" || e.nativeEvent.isComposing || !enterTarget) return;
                            e.preventDefault();
                            setActiveEndpoint(enterTarget.ID);
                        }}
                        placeholder="Filter..."
                        className={styles.endpointFilterInput}
                    />
                </div>

                <div className={styles.endpointList}>
                    {recentEndpoints.length > 0 && (
                        <div className={styles.recentSection}>
                            <div className={styles.recentLabel}>
                                <StatusIcon kind="clock" size={14} className={styles.recentIcon} />
                                Recent
                            </div>
                            {recentEndpoints.map((ep) => (
                                <div
                                    key={`recent-${ep.ID}`}
                                    onClick={() => setActiveEndpoint(ep.ID)}
                                    className={itemClass(ep, true)}
                                >
                                    <span className={styles.endpointItemTitle}>{ep.Title}</span>
                                    {ep.ShortName && ep.ShortName !== ep.Title && (
                                        <span className={styles.endpointItemShortName}>{ep.ShortName}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {recentEndpoints.length === 0 && groups.length === 0 && (
                        <div className={styles.endpointNoMatches}>No matches</div>
                    )}
                    {groups.map((group) => (
                        <div key={group.moduleId}>
                            {(groups.length > 1 || recentEndpoints.length > 0) && (
                                <div className={styles.endpointGroupLabel}>
                                    {group.moduleName}
                                </div>
                            )}
                            {group.endpoints.map((ep) => (
                                <div
                                    key={ep.ID}
                                    onClick={() => setActiveEndpoint(ep.ID)}
                                    className={itemClass(ep, false)}
                                >
                                    <span className={styles.endpointItemTitle}>{ep.Title}</span>
                                    {ep.ShortName && ep.ShortName !== ep.Title && (
                                        <span className={styles.endpointItemShortName}>{ep.ShortName}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
